import moment from "moment";

import { isOption, Option } from "api/common/types/common.types";
import {
  PublicProtectionProfile,
  PublicProtectionProfileFilters,
} from "api/public/protectionProfiles/protectionProfiles.types";
import { filterExact } from "utils/filter";

/**
 * Filters PPs based on archived status:
 * - Approved (archived=false): Publishing status AND not past sunset date
 * - Archived (archived=true): Not Publishing OR past sunset date
 */
export const filterByArchivedStatus = (
  pps: PublicProtectionProfile[],
  archived: boolean,
): PublicProtectionProfile[] => {
  return pps.filter((pp) => {
    const isPastSunset =
      pp.sunset_date && new Date(pp.sunset_date) < new Date();
    const isApproved = pp.status === "Publishing" && !isPastSunset;

    return archived ? !isApproved : isApproved;
  });
};

export const filterByTechType = (
  pps: PublicProtectionProfile[],
  techType: string,
): PublicProtectionProfile[] => {
  return filterExact(pps, "tech_type", techType);
};

export const filterByCcVersion = (
  pps: PublicProtectionProfile[],
  ccVersion: string,
): PublicProtectionProfile[] => {
  return filterExact(pps, "cc_version", ccVersion);
};

export const filterBySunsetDate = (
  pps: PublicProtectionProfile[],
  sunsetDate: string | Option,
): PublicProtectionProfile[] => {
  if (!sunsetDate) return pps;
  const dateValue = isOption(sunsetDate) ? sunsetDate.value : sunsetDate;
  return pps.filter((pp) => pp.sunset_date === dateValue);
};

export const filterBySearch = (
  pps: PublicProtectionProfile[],
  search: string,
): PublicProtectionProfile[] => {
  if (!search) return pps;

  const normalizedSearch = search.toLowerCase().trim();

  const formatDate = (date: string | null): string =>
    date ? moment.utc(date).format("M/D/YYYY") : "";

  return pps.filter((pp) => {
    const searchableFields = [
      pp.pp_name,
      pp.pp_short_name,
      pp.tech_type,
      pp.cc_version,
      pp.pp_sponsor_id,
      formatDate(pp.pp_date),
      formatDate(pp.sunset_date),
      formatDate(pp.pp_transition),
    ];

    return searchableFields.some(
      (field) => field && field.toLowerCase().includes(normalizedSearch),
    );
  });
};

export const filterPps = (
  pps: PublicProtectionProfile[],
  filters: PublicProtectionProfileFilters,
): PublicProtectionProfile[] => {
  let filteredPps = pps;

  filteredPps = filterByArchivedStatus(filteredPps, filters.archived);

  filteredPps = filterByTechType(
    filteredPps,
    filters.tech_type.value.toString(),
  );

  filteredPps = filterByCcVersion(
    filteredPps,
    filters.cc_version.value.toString(),
  );

  filteredPps = filterBySunsetDate(
    filteredPps,
    filters.sunset_date.value.toString(),
  );

  filteredPps = filterBySearch(filteredPps, filters.search);

  return filteredPps;
};
